import './MicroformInputs.scss';

import React, { useEffect, useImperativeHandle, useRef } from 'react';

import classNames from 'classnames';
import { useFormikContext } from 'formik';

import { ccConstants } from '@ulta/core/utils/creditCard/creditCard';
import interpolateString from '@ulta/core/utils/interpolateString/interpolateString';

import Button from '@ulta/modules/Button/Button';
import InputField from '@ulta/modules/InputField/InputField';
import Text from '@ulta/modules/Text/Text';

import { isMicroformLoading } from '../../hooks/useMicroform/useMicroform';
import { useMicroformField } from '../../hooks/useMicroformField/useMicroformField';
import Icon from '../Icon/Icon';
import InlineMessage from '../InlineMessage/InlineMessage';
import LoaderCircular from '../LoaderCircular/LoaderCircular';

export const MicroformInputs = React.forwardRef( ( props, ref ) => {
  const {
    components,
    cardType,
    creditCardNumber,
    encryptionKey,
    expirationDate,
    cvv,
    isSubmitting,
    isCVVApplied,
    isUpdateCard,
    isMaskedCard,
    microform,
    microformReady,
    microformError,
    propsLoading,
    loading,
    selected,
    transientToken,
    handleMicroformNumberChange,
    handleMicroformCVVChange,
    onClearNumber,
    onClearCvv,
    displayOverlay,
    scheduleValidationStatusUpdate,
    creditCardNumberDefaultIcon,
    isEditingNumber,
    isEditingCvv,
    cvvLength
  } = props;

  const { errors, touched, setFieldTouched, values: formValues } = useFormikContext();

  const rawHelpText = cvv?.helpText;
  const cvvHelpText = interpolateString( rawHelpText, [cvvLength] );

  const {
    containerRef: ccNumberContainerRef,
    error: ccNumberError,
    fieldInstance: ccNumberFieldInstance,
    focused: ccNumberFocused,
    valid: ccNumberValid,
    empty: ccNumberEmpty = true,
    couldBeValid: ccNumberCouldBeValid
  } = useMicroformField( {
    microform: microformReady ? microform : null,
    fieldType: transientToken ? 'number' : null,
    selector: '#' + creditCardNumber.name,
    show: true,
    refreshTrigger: displayOverlay,
    onChange: handleMicroformNumberChange,
    title: `${creditCardNumber.label}${creditCardNumber.helpText ? `. ${creditCardNumber.helpText}` : ''}`
  } );

  const previousCcFocused = useRef( false );
  useEffect( () => {
    if( previousCcFocused.current && !ccNumberFocused ){
      setFieldTouched( 'creditCardNumber', true );
    }
    previousCcFocused.current = ccNumberFocused;
  }, [ccNumberFocused, setFieldTouched] );

  const {
    containerRef: cvvContainerRef,
    error: cvvError,
    fieldInstance: cvvFieldInstance,
    focused: cvvFocused,
    valid: cvvValid,
    empty: cvvEmpty = true,
    couldBeValid: cvvCouldBeValid
  } = useMicroformField( {
    microform: microformReady ? microform : null,
    fieldType: transientToken ? 'securityCode' : null,
    selector: '#' + cvv.name,
    maxLength: cvvLength,
    show: components.creditCard.cvv,
    refreshTrigger: displayOverlay,
    onChange: handleMicroformCVVChange,
    placeholder: cvv.placeholderLabel,
    title: `${cvv.label}${cvvHelpText ? `. ${cvvHelpText}` : ''}`
  } );

  useImperativeHandle( ref, () => ( {
    ccNumberFieldInstance,
    cvvFieldInstance
  } ) );

  useEffect( () => {
    if( isEditingCvv && cvvFieldInstance ){
      const timeoutId = setTimeout( () => {
        cvvFieldInstance.focus();
      }, 50 );
      return () => clearTimeout( timeoutId );
    }
  }, [isEditingCvv, cvvFieldInstance] );

  const previousCvvFocused = useRef( false );
  useEffect( () => {
    if( previousCvvFocused.current && !cvvFocused ){
      setFieldTouched( 'cvv', true );
    }
    previousCvvFocused.current = cvvFocused;
  }, [cvvFocused, setFieldTouched] );

  const showFacade = isSubmitting || propsLoading || loading;
  const showCcMask = ( isCVVApplied && !isEditingNumber ) || ( showFacade && !!cardType.type );
  const showCvvMask = ( isCVVApplied && !isEditingCvv ) || showFacade;

  const isCcMasked = isCVVApplied && !isEditingNumber;
  const isCvvMasked = isCVVApplied && !isEditingCvv;

  const showCcError = !isCcMasked && ( ( touched.creditCardNumber && errors.creditCardNumber ) || ccNumberError || ( !( ccNumberValid ?? false ) && !( ccNumberEmpty ?? true ) && ( !ccNumberFocused || !ccNumberCouldBeValid ) ) );
  const ccErrorMessage = ( touched.creditCardNumber && errors.creditCardNumber ) || ccNumberError || creditCardNumber?.validations?.[0]?.message;
  const showCvvError = !isCvvMasked && ( ( touched.cvv && errors.cvv ) || cvvError || ( !( cvvValid ?? false ) && !( cvvEmpty ?? true ) && ( !cvvFocused || !cvvCouldBeValid ) ) );
  const cvvErrorMessage = ( touched.cvv && errors.cvv ) || cvvError || cvv?.validations?.[0]?.message;

  const cvvIcon = ( !cvvEmpty || !!cardType.type || ( isCVVApplied && !isEditingCvv ) ) ? ( cvvLength === 4 ? 'CreditCardAmexFrontView' : 'CreditCardBackView' ) : null;

  return (
    <div className='MicroformInputs'>
      { components.creditCard.input && (
        <div className='MicroformInputs__creditCardNumber'>
          <div className='MicroformInputs__InputField' style={ showCcMask ? { visibility: 'hidden', position: 'absolute', pointerEvents: 'none', width: '100%' } : {} }>
            <div className='MicroformInputs__InputField__label'>
              <label
                htmlFor={ creditCardNumber.name }
                className='MicroformInputs__InputField__label--text'
              >
                { creditCardNumber.label }
              </label>
            </div>
            <div
              className={ classNames( 'MicroformInputs__InputField__content', {
                'MicroformInputs__InputField__content--active': !ccNumberEmpty || showCcMask,
                'MicroformInputs__InputField__content--focused': ccNumberFocused,
                'MicroformInputs__InputField--error': showCcError
              } ) }
            >
              <div className='MicroformInputs__InputField__formControls'>
                { ( !isCVVApplied || isSubmitting ) && !propsLoading && !loading && isMicroformLoading( { transientToken, microformReady, selected, error: microformError, captureContext: encryptionKey?.publicKey } ) && (
                  <div className='MicroformInputs__fieldLoader'>
                    <LoaderCircular size='sm' />
                  </div>
                ) }
                <div className='MicroformInputs__InputField__CreditCardNumber__icon'>
                  <Icon name={ cardType.image || creditCardNumberDefaultIcon }
                    title={ cardType.cardLabel || 'Credit Card' }
                  />
                </div>
                <div className='MicroformInputs__fieldWrapper' style={ { flex: 1, position: 'relative', height: '100%', display: 'flex', alignItems: 'center' } }>
                  <div
                    ref={ ccNumberContainerRef }
                    aria-label={ creditCardNumber.label }
                    aria-describedby={ classNames( {
                      [`${creditCardNumber.name}-help`]: creditCardNumber?.helpText && !showCcError,
                      [`${creditCardNumber.name}-error`]: showCcError
                    } ) }
                    className='MicroformInputs__InputField__input MicroformInputs__microformField'

                    data-testid='microform-cc-number'
                    id={ creditCardNumber.name }
                    style={ showCcMask ? { visibility: 'hidden', position: 'absolute', pointerEvents: 'none' } : { width: '100%' } }
                  />

                </div>
                <div className='MicroformInputs__InputField__actions'>
                  { !ccNumberEmpty && !isCVVApplied && !isSubmitting && (
                    <Button
                      type='button'
                      ariaLabel={ creditCardNumber.clearAccessbilityLabel }
                      iconImage={ 'X' }
                      icon={ true }
                      iconSize={ 'lg' }
                      ariaHiddenIcon={ true }
                      onClick={ () => {
                        ccNumberFieldInstance?.clear();
                        onClearNumber();
                      } }
                      className='MicroformInputs__InputField__clearInputButton'
                    />
                  ) }
                </div>
              </div>
            </div>
            { creditCardNumber?.helpText && !showCcError &&
            <div id={ `${creditCardNumber.name}-help` }>
              <Text
                textStyle='body-3'
                color='neutral-600'
              >{ creditCardNumber?.helpText }
              </Text>
            </div> }
            { showCcError && (
              <div className='MicroformInputs__InputField__message'>
                <InlineMessage
                  messageType='error'
                  message={ ccErrorMessage }
                  id={ `${creditCardNumber.name}-error` }
                  role='alert'
                />
              </div>
            ) }
          </div>
          { showCcMask && (
            <InputField
              name={ creditCardNumber.name }
              label={ creditCardNumber.label }
              type={ creditCardNumber.type }
              icon={ cardType.image }
              iconTitle={ cardType.cardLabel }
              onChange={ () => props.onEditMaskedCard() }
              helpText={ creditCardNumber.helpText }
              iconPosition='left'
            />
          ) }
        </div>
      ) }

      { components.creditCard.dateOrCvv && (
        <div className='MicroformInputs_expDateCVV'>
          { components.creditCard.expirationDate && (
            <div className='MicroformInputs_expDateCVV__expDate'>
              <InputField
                clearAccessbilityLabel={
                  expirationDate?.clearAccessbilityLabel
                }
                helpText={ expirationDate.helpText }
                name={ expirationDate.name }
                type={ expirationDate.type }
                formatter={
                  ccConstants.FORMATTER_PATTERNS.EXPDATE_FORMATTER
                } // TODO: update to come from DXL in supportedCards
                label={ expirationDate.label }
                placeholderLabel={ expirationDate.placeholderLabel }
                disabled={ isCVVApplied || isSubmitting }
                autoComplete='cc-exp'
                onChange={ () => {
                  if( isUpdateCard && isMaskedCard && scheduleValidationStatusUpdate ){
                    scheduleValidationStatusUpdate();
                  }
                } }
              />
            </div>
          ) }

          { components.creditCard.cvv && (
            <div className='MicroformInputs_expDateCVV__cvv'>
              <div className='MicroformInputs__InputField'style={ showCvvMask ? { visibility: 'hidden', position: 'absolute', pointerEvents: 'none' } : { width: '100%' } } >
                <div className='MicroformInputs__InputField__label'>
                  <label
                    htmlFor={ cvv.name }
                    className='MicroformInputs__InputField__label--text'
                  >
                    { cvv.label }
                  </label>
                </div>
                <div
                  className={ classNames( 'MicroformInputs__InputField__content', {
                    'MicroformInputs__InputField__content--active': !cvvEmpty || showCvvMask,
                    'MicroformInputs__InputField__content--focused': cvvFocused,
                    'MicroformInputs__InputField--error': showCvvError
                  } ) }
                >
                  <div className='MicroformInputs__InputField__formControls'>
                    { ( !isCVVApplied || isSubmitting ) && !propsLoading && !loading && isMicroformLoading( { transientToken, microformReady, selected, error: microformError, captureContext: encryptionKey?.publicKey } ) && (
                      <div className='MicroformInputs__fieldLoader'>
                        <LoaderCircular size='sm' />
                      </div>
                    ) }
                    <div className='MicroformInputs__fieldWrapper' style={ { flex: 1, position: 'relative', height: '100%', display: 'flex', alignItems: 'center' } }>
                      <div
                        ref={ cvvContainerRef }
                        aria-label={ cvv.label }
                        aria-describedby={ classNames( {
                          [`${cvv.name}-help`]: cvvHelpText && !showCvvError,
                          [`${cvv.name}-error`]: showCvvError
                        } ) }
                        className='MicroformInputs__InputField__input MicroformInputs__microformField'
                        data-testid='microform-cvv'
                        id={ cvv.name }
                        style={ showCvvMask ? { visibility: 'hidden', position: 'absolute', pointerEvents: 'none' } : { width: '100%' } }
                      />
                    </div>
                    <div className='MicroformInputs__InputField__actions'>
                      { !cvvEmpty && !isCVVApplied && !isSubmitting && (
                        <Button
                          type='button'
                          ariaLabel={ cvv.clearAccessbilityLabel }
                          iconImage={ 'X' }
                          icon={ true }
                          iconSize={ 'lg' }
                          ariaHiddenIcon={ true }
                          onClick={ () => {
                            cvvFieldInstance?.clear();
                            onClearCvv();
                          } }
                          className='MicroformInputs__InputField__clearInputButton'
                        />
                      ) }
                      { cvvIcon && (
                        <div className='MicroformInputs__InputField__CVV__icon'>
                          <Icon name={ cvvIcon }
                            title={ `${cardType.cardLabel}  ${cvv.label}` }
                          />
                        </div>
                      ) }
                    </div>
                  </div>
                </div>
                { cvvHelpText && !showCvvError &&
                <div id={ `${cvv.name}-help` }>
                  <Text
                    textStyle='body-3'
                    color='neutral-600'
                  >{ cvvHelpText }
                  </Text>
                </div> }
                { !isSubmitting && showCvvError && (
                  <div className='MicroformInputs__InputField__message'>
                    <InlineMessage
                      messageType='error'
                      message={ cvvErrorMessage }
                      id={ `${cvv.name}-error` }
                      role='alert'
                    />
                  </div>
                ) }
              </div>
              { showCvvMask && (
                <InputField
                  name={ cvv.name }
                  label={ cvv.label }
                  type={ cvv.type }
                  icon={ cvvIcon }
                  readOnly
                  iconTitle={ `${cardType.cardLabel}  ${cvv.label}` }
                  onChange={ () => props.onEditMaskedCard() }
                  helpText={ cvvHelpText }
                />
              ) }
            </div>
          ) }
        </div>
      ) }
    </div>
  );
} );

MicroformInputs.defaultProps = {
  creditCardNumberDefaultIcon: 'Generic'
};
MicroformInputs.displayName = 'MicroformInputs';
export default MicroformInputs;